import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import { Hoja } from '#components/mobile/ui/Hoja';
import { Icono } from '#components/mobile/ui/Icono';
import { color, densidad, num, texto } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useCategories } from '#hooks/useCategories';
import { useFormat } from '#hooks/useFormat';
import { useOverspentCategories } from '#hooks/useOverspentCategories';
import { SheetNameProvider } from '#hooks/useSheetName';
import { useSheetValue } from '#hooks/useSheetValue';
import { useUndo } from '#hooks/useUndo';
import { envelopeBudget } from '#spreadsheet/bindings';

import {
  bloquesCubrir,
  categoriasCandidatas,
  cubrirTodasDesdeListo,
  faltaPorCubrir,
  importeCubrir,
  LISTO_PARA_ASIGNAR,
  origenesParaCubrir,
  pasoTrasCubrir,
} from './cubrir';
import type { BloqueCubrir, OrigenCubrir } from './cubrir';
import { cuentaDelGasto, repartirOrigenesPorCuenta } from './cuentaCategoria';
import { esPagada, marcaManual } from './pagadas';
import {
  conLinea,
  InterruptorMostrarTodas,
  useLineaTraspaso,
  useMostrarTodas,
} from './PorCuenta';
import { useCuentasCategorias } from './useCuentasCategorias';
import { useDatosObjetivos } from './useDatosObjetivos';
import { usePagadas } from './usePagadas';

/** Tras cubrir a medias, espera como mucho esto a que la hoja se actualice. */
const ESPERA_MAXIMA_MS = 1500;
/** Margen al abrir para que lleguen los saldos antes de decir «no hay». */
const ESPERA_APERTURA_MS = 700;

/** Saldo positivo legible en claro y en oscuro (verde acercado a la tinta). */
const TINTA_SALDO = `color-mix(in oklab, ${color.ok} 72%, ${color.fg})`;

type HojaCubrirProps = {
  month: string;
  onBudgetAction: (month: string, type: string, args: unknown) => unknown;
  /** Abre directamente en «¿De dónde sale?» para esta categoría. */
  categoriaInicial?: string | null;
  onCerrar: () => void;
};

/**
 * «Cubrir» en dos toques: (1) la categoría en rojo, (2) de dónde sale el
 * dinero. Al tocar un origen se cubre al momento (lo que falta o lo que
 * tenga el origen si no llega) con `cover-overspending` y aviso con
 * deshacer. Si cubre todo vuelve a la lista (o se cierra si no queda
 * ninguna); si no llega, se queda para elegir otro origen.
 */
export function HojaCubrir(props: HojaCubrirProps) {
  return (
    <SheetNameProvider name={monthUtils.sheetForMonth(props.month)}>
      <HojaCubrirContenido {...props} />
    </SheetNameProvider>
  );
}

function HojaCubrirContenido({
  month,
  onBudgetAction,
  categoriaInicial,
  onCerrar,
}: HojaCubrirProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const { showUndoNotification } = useUndo();

  const {
    data: { list: categorias, grouped: grupos } = {
      list: [],
      grouped: [],
    },
  } = useCategories();
  const { categories: enRojo, amountsByCategory } = useOverspentCategories({
    month,
  });

  const [elegida, setElegida] = useState<string | null>(
    categoriaInicial ?? null,
  );
  // Cubiertas del todo mientras la hoja aún no se ha enterado: no se
  // enseñan en la lista (salen solas cuando dejan de estar en rojo).
  const [ocultas, setOcultas] = useState<ReadonlySet<string>>(new Set());
  // Cubierta a medias: saldo de antes, para bloquear la lista hasta que
  // llegue el nuevo (y no cubrir dos veces con cifras viejas).
  const [esperando, setEsperando] = useState<{ antes: IntegerAmount } | null>(
    null,
  );
  // Origen vaciado a medias: fuera de la lista mientras siga mostrando el
  // saldo de antes (su celda puede llegar después que la de la categoría).
  const [vaciado, setVaciado] = useState<OrigenCubrir | null>(null);

  useEffect(() => {
    setOcultas(prev => {
      const siguen = [...prev].filter(id => enRojo.some(c => c.id === id));
      return siguen.length === prev.size ? prev : new Set(siguen);
    });
  }, [enRojo]);

  const listaRojo = useMemo(
    () => enRojo.filter(c => !ocultas.has(c.id)),
    [enRojo, ocultas],
  );

  const categoria = categorias.find(c => c.id === elegida) ?? null;
  const saldoHoja = useSheetValue<'envelope-budget', 'leftover'>(
    envelopeBudget.catBalance(elegida ?? ''),
  );
  const saldoElegida =
    saldoHoja ?? (elegida ? amountsByCategory.get(elegida) : undefined);
  const cargando = saldoElegida == null;
  const falta = faltaPorCubrir(saldoElegida);

  useEffect(() => {
    if (!esperando) {
      return;
    }
    if (saldoElegida !== esperando.antes) {
      setEsperando(null);
      return;
    }
    const id = setTimeout(() => setEsperando(null), ESPERA_MAXIMA_MS);
    return () => clearTimeout(id);
  }, [esperando, saldoElegida]);

  const candidatas = useMemo(() => categoriasCandidatas(grupos), [grupos]);
  const datos = useDatosObjetivos(month, candidatas);
  const toBudget = useSheetValue<'envelope-budget', 'to-budget'>(
    envelopeBudget.toBudget,
  );
  // Facturas fijas de este mes aún sin pagar: van al último bloque.
  const { fijos, manuales } = usePagadas(month);
  const pendientes = useMemo(() => {
    const ids = new Set<string>();
    for (const id of fijos) {
      const marca = marcaManual(manuales, id);
      const d = datos.get(id);
      if (
        d &&
        !esPagada({
          fijo: true,
          manual: marca === 'pagada',
          noPagada: marca === 'no-pagada',
          datos: d,
        })
      ) {
        ids.add(id);
      }
    }
    return ids;
  }, [datos, fijos, manuales]);
  const origenes = useMemo(
    () =>
      elegida
        ? origenesParaCubrir({
            grupos,
            saldos: datos,
            toBudget,
            destino: elegida,
            pendientes,
          }).filter(o => !(o.id === vaciado?.id && o.saldo === vaciado.saldo))
        : [],
    [datos, elegida, grupos, pendientes, toBudget, vaciado],
  );
  // Cuenta del gasto (H: cubrir por cuenta): solo se ofrecen las categorías
  // de esa cuenta y las «sin cuenta clara»; las de otra cuenta, plegadas.
  const cuentasCat = useCuentasCategorias(month);
  const cuentaGasto = useMemo(() => {
    if (!elegida || !cuentasCat.listo || cuentasCat.cuentas.length <= 1) {
      return null;
    }
    return cuentaDelGasto({
      historial: cuentasCat.historial.get(elegida) ?? [],
      mes: month,
      cuentaCategoria: cuentasCat.cuentaDe(elegida),
      validas: cuentasCat.validas,
    });
  }, [cuentasCat, elegida, month]);
  const reparto = useMemo(
    () =>
      repartirOrigenesPorCuenta({
        origenes,
        cuentaGasto,
        cuentaDe: cuentasCat.cuentaDe,
        ordenCuentas: cuentasCat.cuentas.map(c => c.id),
      }),
    [cuentaGasto, cuentasCat, origenes],
  );
  const nombreCuenta = (id: string) =>
    cuentasCat.cuentas.find(c => c.id === id)?.name ?? '';
  // «Mostrar todas las categorías»: se recuerda en este dispositivo.
  const [mostrarTodas, setMostrarTodas] = useMostrarTodas();
  const lineaTraspaso = useLineaTraspaso(cuentasCat);

  const titulosBloque: Record<BloqueCubrir, string | null> = {
    listo: null,
    sobra: t('Above target'),
    libre: t('No target'),
    reservado: t('With a target or a bill to pay'),
  };

  // «Cubrir las N desde Listo para asignar» (H25): solo si alcanza.
  const todas = useMemo(
    () =>
      cubrirTodasDesdeListo({
        enRojo: listaRojo.map(c => ({
          id: c.id,
          balance: datos.get(c.id)?.balance ?? amountsByCategory.get(c.id) ?? 0,
          budgeted: datos.get(c.id)?.budgeted ?? 0,
        })),
        toBudget,
      }),
    [amountsByCategory, datos, listaRojo, toBudget],
  );
  const cubrirTodas = () => {
    if (!todas || listaRojo.some(c => !datos.has(c.id))) {
      return;
    }
    void onBudgetAction(month, 'budget-amounts', { amounts: todas.amounts });
    showUndoNotification({
      message: t('Covered {{amount}} in {{count}} categories from To Budget.', {
        amount: format(todas.total, 'financial'),
        count: todas.amounts.length,
      }),
    });
    onCerrar();
  };

  // Al abrir, los saldos llegan de la hoja en unos instantes: no decir
  // «no hay dinero» antes de tiempo.
  const [recienAbierta, setRecienAbierta] = useState(true);
  useEffect(() => {
    const id = setTimeout(() => setRecienAbierta(false), ESPERA_APERTURA_MS);
    return () => clearTimeout(id);
  }, []);

  const nombreOrigen = (o: OrigenCubrir) => o.nombre ?? t('To Budget');
  const filaOrigen = (o: OrigenCubrir) => (
    <Fila
      key={o.id}
      nombre={nombreOrigen(o)}
      testId={`cubrir-origen-${o.id}`}
      destacada={o.id === LISTO_PARA_ASIGNAR}
      deshabilitada={esperando != null}
      aviso={
        o.aviso === 'factura'
          ? t('Bill not paid yet')
          : o.aviso === 'objetivo'
            ? t('You would fall short of its target')
            : undefined
      }
      onPress={() => cubrir(o)}
    >
      <Text style={{ ...densidad.cifra, color: TINTA_SALDO }}>
        {format(o.saldo, 'financial')}
      </Text>
    </Fila>
  );
  const otrasEnRojo = listaRojo.filter(c => c.id !== elegida).length;

  const cubrir = (origen: OrigenCubrir) => {
    if (!categoria || esperando) {
      return;
    }
    const importe = importeCubrir(falta, origen.saldo);
    if (importe <= 0) {
      return;
    }
    void onBudgetAction(month, 'cover-overspending', {
      to: categoria.id,
      from: origen.id,
      amount: importe,
      currencyCode: format.currency.code,
    });
    showUndoNotification({
      message: conLinea(
        t(
          'Covered {{amount}} of {{toCategoryName}} with {{fromCategoryName}}.',
          {
            amount: format(importe, 'financial'),
            toCategoryName: categoria.name,
            fromCategoryName: nombreOrigen(origen),
          },
        ),
        // Solo con «Mostrar todas»: dinero de otra cuenta.
        origen.id === LISTO_PARA_ASIGNAR
          ? null
          : lineaTraspaso(cuentasCat.cuentaDe(origen.id), cuentaGasto),
      ),
    });
    const paso = pasoTrasCubrir({ falta, importe, otrasEnRojo });
    if (paso === 'quedarse') {
      setEsperando({ antes: saldoElegida ?? 0 });
      setVaciado(origen);
    } else if (paso === 'lista') {
      setOcultas(prev => new Set(prev).add(categoria.id));
      setElegida(null);
    } else {
      onCerrar();
    }
  };

  return (
    <Hoja aria-label={t('Cover')} data-testid="hoja-cubrir" onCerrar={onCerrar}>
      {!categoria ? (
        <>
          <Text style={{ ...texto.titulo, color: color.fg, marginBottom: 4 }}>
            <Trans>Cover</Trans>
          </Text>
          <Text
            style={{ ...texto.secundario, color: color.fg2, marginBottom: 8 }}
          >
            <Trans>Tap the category you want to cover</Trans>
          </Text>
          {todas && listaRojo.length > 1 && (
            <Fila
              nombre={t('Cover all {{count}} from To Budget', {
                count: listaRojo.length,
              })}
              testId="cubrir-todas"
              destacada
              onPress={cubrirTodas}
            >
              <Text style={{ ...densidad.cifra, color: TINTA_SALDO }}>
                {format(todas.total, 'financial')}
              </Text>
            </Fila>
          )}
          {/* Vacía solo un instante, mientras se leen los saldos: al cubrir
              la última la hoja se cierra sola. */}
          {listaRojo.map(c => (
            <Fila
              key={c.id}
              nombre={c.name}
              testId={`cubrir-rojo-${c.id}`}
              onPress={() => setElegida(c.id)}
            >
              <Text style={{ ...densidad.cifra, color: color.bad }}>
                {format(amountsByCategory.get(c.id) ?? 0, 'financial')}
              </Text>
            </Fila>
          ))}
        </>
      ) : (
        <>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              marginBottom: 8,
            }}
          >
            {otrasEnRojo > 0 && (
              <Button
                variant="bare"
                aria-label={t('Back')}
                data-testid="cubrir-volver"
                onPress={() => setElegida(null)}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 18,
                  backgroundColor: color.surface2,
                  color: color.fg,
                  flexShrink: 0,
                }}
              >
                <Icono nombre="cl" size={18} />
              </Button>
            )}
            <View style={{ minWidth: 0, flex: 1 }}>
              <Text style={{ ...texto.titulo, color: color.fg }}>
                <Trans>Where does it come from?</Trans>
              </Text>
              <PrivacyFilter>
                <Text
                  data-testid="cubrir-falta"
                  style={{ ...texto.secundario, color: color.fg2 }}
                >
                  {cargando
                    ? '\u00a0'
                    : falta > 0
                      ? t('{{categoryName}} needs {{amount}}', {
                          categoryName: categoria.name,
                          amount: format(falta, 'financial'),
                        })
                      : t('{{categoryName}} is no longer in the red', {
                          categoryName: categoria.name,
                        })}
                </Text>
              </PrivacyFilter>
              {cuentaGasto && (
                <Text
                  data-testid="cubrir-cuenta-gasto"
                  style={{ ...texto.secundario, color: color.fg2 }}
                >
                  {t('Spending from {{accountName}}', {
                    accountName: nombreCuenta(cuentaGasto),
                  })}
                </Text>
              )}
            </View>
          </View>
          {falta > 0 &&
            (origenes.length === 0 ? (
              recienAbierta || toBudget == null ? null : (
                <Vacio>
                  <Trans>No categories with money available</Trans>
                </Vacio>
              )
            ) : (
              <>
                {reparto.visibles.length === 0 && cuentaGasto && (
                  <Vacio>
                    {t('No categories of {{accountName}} with money', {
                      accountName: nombreCuenta(cuentaGasto),
                    })}
                  </Vacio>
                )}
                {bloquesCubrir(reparto.visibles).map(
                  ({ bloque, origenes: lista }) => (
                    <View key={bloque} data-testid={`cubrir-bloque-${bloque}`}>
                      {titulosBloque[bloque] && (
                        <Etiqueta>{titulosBloque[bloque]}</Etiqueta>
                      )}
                      {lista.map(filaOrigen)}
                    </View>
                  ),
                )}
                {mostrarTodas &&
                  reparto.otras.map(({ cuenta, origenes: lista }) => (
                    <View key={cuenta} data-testid={`cubrir-cuenta-${cuenta}`}>
                      <Etiqueta>{nombreCuenta(cuenta)}</Etiqueta>
                      {lista.map(filaOrigen)}
                    </View>
                  ))}
              </>
            ))}
          {cuentaGasto && (
            <InterruptorMostrarTodas
              id="cubrir-mostrar-todas"
              isOn={mostrarTodas}
              onToggle={setMostrarTodas}
            />
          )}
        </>
      )}
    </Hoja>
  );
}

function Fila({
  nombre,
  testId,
  destacada,
  deshabilitada,
  aviso,
  onPress,
  children,
}: {
  nombre: string;
  testId: string;
  destacada?: boolean;
  deshabilitada?: boolean;
  /** Línea pequeña bajo el nombre (p. ej. «Tiene objetivo»). */
  aviso?: string;
  onPress: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      variant="bare"
      data-testid={testId}
      isDisabled={deshabilitada}
      onPress={onPress}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        minHeight: densidad.altoFila,
        padding: '0 4px',
        borderRadius: 0,
        boxShadow: `inset 0 -1px 0 ${color.line}`,
        backgroundColor: 'transparent',
        color: color.fg,
        opacity: deshabilitada ? 0.5 : 1,
      }}
    >
      <View style={{ minWidth: 0, flex: 1, alignItems: 'flex-start' }}>
        <Text
          style={{
            ...densidad.nombre,
            fontWeight: destacada ? 700 : densidad.nombre.fontWeight,
            maxWidth: '100%',
            textAlign: 'left',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {nombre}
        </Text>
        {aviso && (
          <Text
            style={{ ...texto.secundario, fontSize: 12, color: color.warn }}
          >
            {aviso}
          </Text>
        )}
      </View>
      <PrivacyFilter>
        <View style={{ ...num, flexShrink: 0 }}>{children}</View>
      </PrivacyFilter>
    </Button>
  );
}

function Etiqueta({ children }: { children: ReactNode }) {
  return (
    <Text
      style={{
        ...texto.etiqueta,
        color: color.fg2,
        padding: '14px 4px 4px',
      }}
    >
      {children}
    </Text>
  );
}

function Vacio({ children }: { children: ReactNode }) {
  return (
    <Text
      style={{
        ...texto.cuerpo,
        color: color.fg2,
        textAlign: 'center',
        padding: '24px 8px',
      }}
    >
      {children}
    </Text>
  );
}
